import { Alert, AlertDescription, AlertTitle } from '@design-system';
import { QUOTE_AGE_RANGE } from '@shared';
import { useIsSelfFundEntryOffered } from '../hooks/use-self-fund-entry';
import { stateName } from '../lib/format';
import type { ConsumerStepId } from '../lib/steps';
import { OtherWaysToPlan } from './other-ways-to-plan';

const REASON_COPY: Partial<Record<GuidedPlanningNoQuotesReason, (state: string) => string>> = {
  state_not_supported: state => `We don’t have insurance quotes in ${state} yet.`,
  age_out_of_range: () =>
    `Insurance quotes are available from age ${QUOTE_AGE_RANGE.min} to ${QUOTE_AGE_RANGE.max}, so there aren’t any for your age yet.`,
  demographics_incomplete: () =>
    'We need a few more intake answers (like your date of birth and ZIP code) before we can quote.',
};

/** Why there are no quotes, with the other ways forward so the consumer is never stuck. */
export function NoQuotesNotice({
  clientId,
  returnTo,
  reason,
  usaState,
}: {
  clientId: string;
  returnTo: ConsumerStepId;
  reason: GuidedPlanningNoQuotesReason | undefined;
  usaState: string | null;
}) {
  const isSelfFundOffered = useIsSelfFundEntryOffered(clientId);
  const message =
    (reason && REASON_COPY[reason]?.(stateName(usaState))) ??
    'We couldn’t find quotes that match your details right now.';
  const otherWays = isSelfFundOffered
    ? 'You can still have a policy you own reviewed, or plan to self-fund your care.'
    : 'You can still have a policy you own reviewed.';

  return (
    <>
      <Alert variant="info" data-testid="consumer-no-quotes">
        <AlertTitle>No insurance options to show yet</AlertTitle>
        <AlertDescription>
          {message} {otherWays}
        </AlertDescription>
      </Alert>
      <OtherWaysToPlan clientId={clientId} returnTo={returnTo} />
    </>
  );
}
